<template>
    <div>
        <ForcastButton :size="'l'" @click="isActive = true"> <img src="../../../../static/img/upload-xls.svg" alt="upload xls" /> </ForcastButton>

        <ModalDefault
            size="m"
            v-if="isActive"
            :with-content="true"
            @close="reload()"
            :theme="true"
            :fix-height="false"
            :fix-width="false"
            :loading="loading"
        >
            <template slot="main">
                <p class="import-xls-title">{{ $t("launchForecast.xlsImport.title") }}</p>

                <div class="mt-4 d-flex align-items-center">
                    <input id="fileInput" type="file" accept=".xlsx, .xls" style="display:none;" v-on:change="onFileChanged($event)" />
                    <span class="import-xls-input mr-3" style="cursor:pointer;" v-b-tooltip="filename" @click.stop="handleFileInput()">
                        {{ filename }}
                    </span>
                    <ForcastButton class="mr-3 flex-shrink-0" @click.stop="handleFileInput()" :size="'l'">{{
                        $t("launchForecast.xlsImport.browse")
                    }}</ForcastButton>
                    <ForcastButton class="flex-shrink-0" @click.stop="uploadFile()" :size="'l'" :disabled="!file">{{
                        $t("launchForecast.xlsImport.upload")
                    }}</ForcastButton>
                </div>

                <div v-if="totalImported > 0">
                    <p class="import-xls-result mt-5 text-center">
                        {{ $t("launchForecast.xlsImport.matchResult", { matched: items.length, total: totalImported }) }}
                    </p>

                    <div class="mb-4 mt-5 import-xls-container-result">
                        <table class="import-xls-table">
                            <thead>
                                <tr>
                                    <th class="text-center">{{ $t("launchForecast.xlsImport.clientSapRef") }}</th>
                                    <th class="text-center">
                                        {{ $t("launchForecast.xlsImport.forecastAction") }}
                                        <span class="info-icon" v-b-tooltip.hover.html="forecastActionTooltip"
                                            ><i class="fa fa-info-circle"></i
                                        ></span>
                                    </th>
                                    <th class="text-center">{{ $t("launchForecast.xlsImport.currentPrice") }}</th>
                                    <th class="text-center">{{ $t("launchForecast.xlsImport.priceToImport") }}</th>
                                    <th class="text-center">
                                        {{ $t("launchForecast.xlsImport.priceAction") }}
                                        <span class="info-icon" v-b-tooltip.hover.html="priceActionTooltip"><i class="fa fa-info-circle"></i></span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="item in tableItems" :key="item.ref">
                                    <td class="text-center font-weight-bold m-0">{{ item.ref }}</td>
                                    <td class="text-center m-0">{{ formatForecastAction(item.forecastAction) }}</td>
                                    <td class="text-center m-0">{{ item.forecastPrice != null ? item.forecastPrice : "-" }}</td>
                                    <td class="text-center m-0">{{ item.filePrice != null ? item.filePrice : "-" }}</td>
                                    <td class="text-center m-0">{{ formatPriceAction(item.priceAction, item.clientType) }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div v-if="itemsNotExist.length > 0" class="unknown-refs-list">
                        <p>{{ $t("launchForecast.xlsImport.unknownRefs") }}</p>
                        <p v-for="itemNotExist in itemsNotExist" :key="itemNotExist" class="font-weight-bold m-0">{{ itemNotExist }}</p>
                    </div>

                    <div class="row mt-5">
                        <div class="col">
                            <ForcastButton @click="reload()" :size="'l'">{{ $t("launchForecast.xlsImport.cancel") }}</ForcastButton>
                        </div>
                        <div class="col text-right">
                            <ForcastButton @click.stop="updateItems()" :size="'l'">{{ $t("launchForecast.xlsImport.startImport") }}</ForcastButton>
                        </div>
                    </div>
                </div>
            </template>
        </ModalDefault>
    </div>
</template>
<script>
import TryLoad from "@/mixins/TryLoadMixin";
import ForcastButton from "@/components/forecasts/elements/form/ForcastButton";
import ModalDefault from "@/components/ui/ModalDefault";
import forecastsService from "@/components/forecasts/forecastsService";

export default {
    components: {
        ModalDefault,
        ForcastButton
    },
    mixins: [TryLoad],
    props: {
        forecast: {
            type: Object,
            required: true,
            default: undefined
        }
    },
    data() {
        return {
            isActive: false,
            loading: false,
            items: [],
            itemsXlsChecking: [],
            itemsToInsert: [],
            itemsAlreadyExist: [],
            itemsNotExist: [],
            totalImported: 0,
            file: null,
            filename: ""
        };
    },
    computed: {
        forecastActionTooltip() {
            return this.$t("launchForecast.xlsImport.forecastActionTooltip");
        },
        priceActionTooltip() {
            return this.$t("launchForecast.xlsImport.priceActionTooltip");
        },
        tableItems() {
            const fromItems = Array.isArray(this.items)
                ? this.items.map(i => ({
                      id: i.componentId,
                      ref: i.ref,
                      forecastAction: i.forecastAction != null ? i.forecastAction : null,
                      forecastPrice: i.forecastPrice != null ? i.forecastPrice : null,
                      filePrice: i.filePrice != null ? i.filePrice : null,
                      priceAction: i.priceAction != null ? i.priceAction : null,
                      clientType: i.clientType != null ? i.clientType : null
                  }))
                : [];

            return [...fromItems];
        }
    },
    methods: {
        formatForecastAction(action) {
            if (action === "add") return this.$t("launchForecast.xlsImport.toAdd");
            if (action === "none") return this.$t("launchForecast.xlsImport.existingRef");
            return action || "-";
        },
        formatPriceAction(action, clientType) {
            if (action === "add") return this.$t("launchForecast.xlsImport.toAdd");
            if (action === "update") return this.$t("launchForecast.xlsImport.toUpdate");
            if (action === "remove") return this.$t("launchForecast.xlsImport.toRemove");
            if (action === "none") {
                if (clientType && clientType.toUpperCase() !== "Z001") {
                    return this.$t("launchForecast.xlsImport.notImportedType", { type: clientType });
                }
                return this.$t("launchForecast.xlsImport.notImported");
            }
            return action || "-";
        },
        handleFileInput() {
            document.getElementById("fileInput").click();
        },
        onFileChanged(event) {
            const files = event.target.files;
            this.filename = files[0].name;
            this.file = files[0];
        },
        async uploadFile() {
            this.loading = true;
            this.itemsXlsChecking = await forecastsService.uploadXls(this.forecast, this.file);
            this.dialogSuccess(this.$t("launchForecast.xlsImport.uploadSuccess"));

            this.items = this.itemsXlsChecking["items"];
            this.itemsNotExist = this.itemsXlsChecking["notFoundRefs"];
            this.totalImported = this.items.length + this.itemsNotExist.length;

            this.itemsToInsert = Array.isArray(this.items) ? this.items.filter(i => i && i.forecastAction === "add") : [];
            this.itemsAlreadyExist = Array.isArray(this.items) ? this.items.filter(i => i && i.forecastAction === "none") : [];

            this.loading = false;
        },
        updateItems() {
            this.loading = true;
            forecastsService
                .updateItemsFromImport(this.forecast, this.items)
                .then(() => {
                    this.loading = false;
                    this.dialogSuccess(this.$t("launchForecast.xlsImport.importSuccess"));
                    this.reload();
                    this.$emit("reloadItems");
                })
                .catch(() => {
                    this.loading = false;
                    this.$swal({
                        text: this.$t("launchForecast.xlsImport.errorImport"),
                        type: "error",
                        confirmButtonText: "OK",
                        confirmButtonColor: "rgb(135, 135, 135)",
                        allowOutsideClick: false,
                        allowEscapeKey: false
                    });
                });
        },
        reload() {
            this.$emit("reload");
            this.isActive = true;
        }
    }
};
</script>
<style lang="scss" scoped>
@import "../../../assets/css/themes";
@import "../css/forecasts";
a {
    text-decoration: underline;
}
.import-xls-title,
.import-xls-result {
    font-size: 32px;
    font-family: "Oswald", sans-serif;
}
.import-xls-input {
    display: inline-block;
    text-transform: inherit;
    color: #888;
    flex: 1;
    min-width: 0;
    height: 41px;
    padding: 10px 18px;
    border: rgb(135, 135, 135) 2px solid;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.import-xls-container-result {
    max-height: 36vh;
    overflow: auto;
}
.import-xls-table {
    width: 100%;
    border-collapse: collapse;
}
.import-xls-table th,
.import-xls-table td {
    border: 1px solid #ddd;
    padding: 8px;
}
.import-xls-table th {
    background-color: #f2f2f2;
    text-align: center;
}
.unknown-refs-list {
    max-height: 20vh;
    overflow: auto;
}
.info-icon {
    cursor: help;
    color: #888;
    margin-left: 4px;
    vertical-align: middle;
}
</style>



// WEBPACK FOOTER //
// ForecastXlsImport.vue?24ed377e